import { BridgeComponent } from "@hotwired/hotwire-native-bridge";
import { SupercastTimeUpdateEvent } from "utils/events";

export default class PlayButtonController extends BridgeComponent {
  static component = "play-button";
  static targets = ["play", "pause", "locked", "loading"];
  static values = {
    feedItemUrl: String,
    itemId: Number,
    itemType: String,
    audioUrl: String,
    hlsUrl: String,
    timestamp: Number,
    lockOnPlay: Boolean,
    resolutionUrl: String,
    name: String,
    channelName: String,
    summary: String,
    artworkUrl: String
  };

  connect() {
    this.playbackDataPromise = null;
    this.locked = !this.hasPlayTarget;
    this.resolvePlaybackData().finally(() => {
      this.send("connect", { feedItemUrl: this.feedItemUrlValue });
    });
    this.boundTimeUpdate = this.timeUpdate.bind(this);
    window.addEventListener("supercast-timeupdate", this.boundTimeUpdate);
  }

  disconnect() {
    if (this.boundTimeUpdate) {
      window.removeEventListener("supercast-timeupdate", this.boundTimeUpdate);
    }
  }

  openResolution(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.hasResolutionUrlValue || !this.resolutionUrlValue) {
      return;
    }

    const url = new URL(this.resolutionUrlValue, window.location.origin);
    window.location.href = url.toString();
  }

  timeUpdate(event) {
    if (event.detail.itemId == this.itemIdValue && event.detail.itemType == this.itemTypeValue) {
      if (event.detail.isPlaying) {
        this.updatePlayButton("playing");
      } else {
        this.updatePlayButton("paused");
      }
    } else if (this.locked) {
      this.updatePlayButton("locked");
    } else {
      this.updatePlayButton("paused");
    }
  }

  updatePlayButton(state) {
    // Hide all targets
    if (this.hasPlayTarget) { this.playTarget.hidden = true; }
    if (this.hasPauseTarget) { this.pauseTarget.hidden = true; }
    if (this.hasLockedTarget) { this.lockedTarget.hidden = true; }
    if (this.hasLoadingTarget) { this.loadingTarget.hidden = true; }

    if (state === "playing") {
      if (this.hasPauseTarget) { this.pauseTarget.hidden = false; }
    } else if (state === "paused") {
      if (this.hasPlayTarget) { this.playTarget.hidden = false; }
    } else if (state === "loading") {
      if (this.hasLoadingTarget) { this.loadingTarget.hidden = false; }
    } else if (state === "locked") {
      if (this.hasLockedTarget) { this.lockedTarget.hidden = false; }
    } else {
      console.error("Unknown play button state:", state);
    }
  }

  async play(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    return this.playNow();
  }

  async playNow() {
    this.updatePlayButton("loading");

    await this.resolvePlaybackData();
    if (this.locked) {
      return;
    }

    const timestamp = await this.getTimestamp();
    this.send("play", {
      feedItemUrl: this.feedItemUrlValue,
      name: this.nameValue,
      channelName: this.channelNameValue,
      summary: this.summaryValue,
      artworkUrl: this.artworkUrlValue,
      hlsUrl: this.hlsUrlValue,
      audioUrl: this.audioUrlValue,
      startIfPaused: true,
      timestamp: timestamp || 0,
      itemId: this.itemIdValue,
      itemType: this.itemTypeValue,
      metadataUrl: this.metadataUrl()
    });

    window.queueClient
      .dequeueIfHead({ itemId: this.itemIdValue, itemType: this.itemTypeValue })
      .catch(console.error);
  }

  // Absolute URL so native can fetch it directly. Carries the current list
  // context, since "next" is defined by sort/channel/playlist_id server-side.
  metadataUrl() {
    if (!this.itemIdValue) return "";
    const url = new URL(
      `/subscriber_v2/episodes/${this.itemIdValue}/metadata.json`,
      window.location.origin
    );
    const search = new URLSearchParams(window.location.search);
    for (const key of ["sort", "channel", "playlist_id"]) {
      const value = search.get(key);
      if (value) url.searchParams.set(key, value);
    }
    return url.toString();
  }

  async getTimestamp() {
    if (this.timestampValue && this.timestampValue > 0) {
      return this.timestampValue;
    }

    const playbackData = await window.playbackDb.getPlaybackData(this.itemTypeValue, this.itemIdValue);
    if (playbackData) {
      const shouldRestart = playbackData.duration - playbackData.currentTime < 15;
      return shouldRestart ? 0 : playbackData.currentTime;
    }

    return 0;
  }

  pause(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.send("pause", { feedItemUrl: this.feedItemUrlValue, timestamp: this.timestampValue }, (e) => {
      if (this.hasPlayTarget) { this.playTarget.hidden = false; }
      if (this.hasPauseTarget) { this.pauseTarget.hidden = true; }
    });
  }

  extractFeedItemId() {
    const source = this.element.getAttribute("href") || this.feedItemUrlValue || "";
    const match = source.match(/\/subscriber_v2\/feed_items\/(\d+)/);
    return match ? match[1] : null;
  }

  extractTimestamp() {
    if (this.hasTimestampValue) {
      return this.timestampValue;
    }

    const source = this.element.getAttribute("href") || this.feedItemUrlValue;
    if (!source) {
      return 0;
    }

    try {
      const url = new URL(source, window.location.origin);
      const value = parseInt(url.searchParams.get("timestamp"), 10);
      return Number.isFinite(value) ? value : 0;
    } catch (_error) {
      return 0;
    }
  }

  async resolvePlaybackData() {
    this.timestampValue = this.extractTimestamp();

    if ((this.hasAudioUrlValue && this.audioUrlValue) && (this.hasItemIdValue && this.itemIdValue)) {
      if (!this.hasItemTypeValue || !this.itemTypeValue) {
        this.itemTypeValue = "Episode";
      }
      return true;
    }

    if (this.playbackDataPromise) {
      return this.playbackDataPromise;
    }

    const feedItemId = this.extractFeedItemId();
    if (!feedItemId) {
      return false;
    }

    this.playbackDataPromise = fetch(`/subscriber_v2/feed_items/${feedItemId}.json`, {
      headers: { Accept: "application/json" },
      credentials: "include"
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) {
          return false;
        }

        if (data.locked || data.can_play === false) {
          this.locked = true;
          this.resolutionUrlValue = data.resolution_url;
          this.setActionToOpenResolution();
          this.markLockedMention();
          return false;
        }

        this.feedItemUrlValue = data.feed_item_url || `/subscriber_v2/feed_items/${feedItemId}`;
        this.itemIdValue = Number(data.item_id || feedItemId);
        this.itemTypeValue = data.item_type || "Episode";
        if (data.audio_url) this.audioUrlValue = data.audio_url;
        if (data.hls_url) this.hlsUrlValue = data.hls_url;
        if (data.name) this.nameValue = data.name;
        if (data.channel_name) this.channelNameValue = data.channel_name;
        if (data.summary) this.summaryValue = data.summary;
        if (data.artwork_url) this.artworkUrlValue = data.artwork_url;

        return this.hasAudioUrlValue && !!this.audioUrlValue;
      })
      .catch(() => false)
      .finally(() => {
        this.playbackDataPromise = null;
      });

    return this.playbackDataPromise;
  }

  markLockedMention() {
    if (this.element.tagName !== "A") {
      return;
    }

    const text = this.element.textContent || "";
    if (!text.trim().startsWith("🔒")) {
      this.element.textContent = `🔒 ${text}`;
    }
  }

  setActionToOpenResolution() {
    const currentAction = this.element.getAttribute("data-action") || "";
    if (currentAction.includes("click->play-button#openResolution")) {
      return;
    }

    const actions = currentAction.trim().split(/\s+/).filter(Boolean);
    let replacedPlayAction = false;

    const normalizedActions = actions.map((action) => {
      if (action === "click->play-button#play" || action === "play-button#play") {
        replacedPlayAction = true;
        return "click->play-button#openResolution";
      }

      return action;
    });

    if (!replacedPlayAction) {
      normalizedActions.push("click->play-button#openResolution");
    }

    this.element.setAttribute("data-action", [...new Set(normalizedActions)].join(" "));
  }
}